/**
 * The player's state machine, as a pure reducer.
 *
 * Everything a player does that can go wrong is a state transition, and every one of them is
 * testable here without a DOM, an `<audio>` element, or a browser. The component in
 * `useAudioPlayer.ts` is then only responsible for talking to the element and dispatching.
 *
 * The behaviours worth stating, because each has a failure mode:
 *
 * - **`next` at the end of the queue stops**, it does not wrap and it does not throw. A player
 *   that silently loops makes "previous" feel broken.
 * - **`previous` restarts the current track after 3 seconds**, the convention every music player
 *   uses. Below that it goes back a track.
 * - **Playback carries across navigation**: the state lives outside any route, so nothing here
 *   is keyed to a page.
 * - **A failed item does not clear the queue.** One unplayable inscription must not stop a
 *   session; the queue keeps its place and the item is marked.
 * - **Seeking is only meaningful when a duration is known.** For MIDI and tracker modules there
 *   is no waveform and no decodable duration in the browser, so `canSeek` is false and the UI
 *   must not offer a scrubber that cannot work.
 */

/** How a track is presented, and how much the player can actually do with it. */
export type PlaybackKind =
  /** A container a browser `<audio>` element can play and seek. */
  | 'waveform'
  /** Identified and inspectable, but not playable in the browser: MIDI, XM, MOD. */
  | 'module'
  /** Declared media whose bytes carry nothing. Never offered. */
  | 'none';

export interface Track {
  /** Canonical inscription id. The only identity the catalog uses. */
  id: string;
  /** Display title: a curated or community proposal if one exists, else a derived name. */
  title: string;
  /** Where the title came from, so the UI can label it honestly. */
  titleSource: 'curated' | 'community' | 'derived';
  /** Verified container, e.g. `audio/mpeg`. */
  container?: string;
  durationMs?: number;
  blockHeight?: number;
  /** True when the bytes are on Bitcoin and resolvable. */
  playable: boolean;
  kind: PlaybackKind;
}

export type Repeat = 'off' | 'all' | 'one';

export interface PlayerState {
  queue: Track[];
  /** Index into `queue`, or -1 when nothing is selected. */
  current: number;
  playing: boolean;
  /** Current position in ms, as reported by the element. */
  positionMs: number;
  /** Duration in ms, preferring the catalog's value and falling back to the element's. */
  durationMs: number;
  volume: number;
  muted: boolean;
  repeat: Repeat;
  /** Ids that failed to load, so the UI can mark them rather than silently skipping. */
  failed: string[];
}

export const initialPlayerState: PlayerState = {
  queue: [],
  current: -1,
  playing: false,
  positionMs: 0,
  durationMs: 0,
  volume: 1,
  muted: false,
  repeat: 'off',
  failed: [],
};

export type Action =
  | { type: 'enqueue'; tracks: Track[] }
  | { type: 'playNow'; tracks: Track[]; index: number }
  | { type: 'play'; index: number }
  | { type: 'toggle' }
  | { type: 'pause' }
  | { type: 'next' }
  | { type: 'previous' }
  | { type: 'seek'; positionMs: number }
  | { type: 'tick'; positionMs: number; durationMs?: number }
  | { type: 'ended' }
  | { type: 'setVolume'; volume: number }
  | { type: 'toggleMute' }
  | { type: 'setRepeat'; repeat: Repeat }
  | { type: 'failed'; id: string }
  | { type: 'clear' };

/** The convention every music player uses: past this point, "previous" restarts the track. */
export const RESTART_THRESHOLD_MS = 3000;

export function currentTrack(state: PlayerState): Track | undefined {
  return state.queue[state.current];
}

/**
 * Whether the transport can seek the current item.
 *
 * A browser cannot decode MIDI or a tracker module, so there is no duration to scrub and no
 * position to report. Offering a scrubber anyway is the kind of UI lie this whole phase has been
 * about removing.
 */
export function canSeek(state: PlayerState): boolean {
  const track = currentTrack(state);
  return !!track && track.kind === 'waveform' && durationOf(state, track) > 0;
}

/**
 * The duration to scrub against.
 *
 * **The element's duration wins once it has one, because that is the range you can actually seek
 * within** — seeking operates on the decoded media, not on the catalog. The catalog's probed
 * duration is the fallback, and it is what lets the UI show a scrubber before metadata arrives.
 *
 * When the two disagree, the element's limit is the operative one; a disagreement is itself worth
 * knowing about, which is why the catalog's value is kept on the track rather than overwritten.
 */
export function durationOf(state: PlayerState, track: Track | undefined): number {
  if (!track) return 0;
  if (state.durationMs > 0) return state.durationMs;
  return track.durationMs ?? 0;
}

export function reducer(state: PlayerState, action: Action): PlayerState {
  switch (action.type) {
    case 'enqueue': {
      // Append, de-duplicating by id so a queue built from two pages does not double up. The
      // first occurrence wins, so enqueue order is preserved.
      const seen = new Set(state.queue.map((t) => t.id));
      const added = action.tracks.filter((t) => !seen.has(t.id));
      const queue = [...state.queue, ...added];
      return {
        ...state,
        queue,
        // Selecting the first item of an empty queue is a convenience, not a behaviour change.
        current: state.current === -1 && queue.length > 0 ? 0 : state.current,
      };
    }

    case 'playNow': {
      const queue = action.tracks;
      if (queue.length === 0) return state;
      const index = Math.max(0, Math.min(action.index, queue.length - 1));
      return { ...state, queue, current: index, playing: true, positionMs: 0 };
    }

    case 'play': {
      if (action.index < 0 || action.index >= state.queue.length) return state;
      const switching = action.index !== state.current;
      return {
        ...state,
        current: action.index,
        playing: true,
        positionMs: switching ? 0 : state.positionMs,
      };
    }

    case 'toggle': {
      if (state.current === -1) return state;
      return { ...state, playing: !state.playing };
    }

    case 'pause':
      return { ...state, playing: false };

    case 'next': {
      const { queue, current, repeat } = state;
      if (queue.length === 0) return state;
      if (repeat === 'one') {
        // Repeat-one replays the same track from the start, which is what the control means.
        return { ...state, positionMs: 0, playing: true };
      }
      const nextIndex = current + 1;
      if (nextIndex >= queue.length) {
        if (repeat === 'all') {
          return { ...state, current: 0, positionMs: 0, playing: true };
        }
        // At the end with repeat off: stop on the last item. Do not wrap, do not throw.
        return { ...state, playing: false, positionMs: state.durationMs };
      }
      return { ...state, current: nextIndex, positionMs: 0, playing: true };
    }

    case 'previous': {
      const { queue, current } = state;
      if (queue.length === 0) return state;
      // Past the restart threshold, "previous" means "start this one again".
      if (state.positionMs > RESTART_THRESHOLD_MS) {
        return { ...state, positionMs: 0 };
      }
      const prevIndex = current - 1;
      if (prevIndex < 0) {
        // Before the first item, restart it rather than doing nothing.
        return { ...state, positionMs: 0 };
      }
      return { ...state, current: prevIndex, positionMs: 0, playing: true };
    }

    case 'seek': {
      if (!canSeek(state)) return state;
      const limit = durationOf(state, currentTrack(state));
      const clamped = Math.max(0, Math.min(action.positionMs, limit));
      return { ...state, positionMs: clamped };
    }

    case 'tick': {
      const durationMs = action.durationMs && action.durationMs > 0
        ? action.durationMs
        : state.durationMs;
      return { ...state, positionMs: action.positionMs, durationMs };
    }

    case 'ended':
      return reducer({ ...state, playing: false }, { type: 'next' });

    case 'setVolume':
      return { ...state, volume: Math.max(0, Math.min(1, action.volume)), muted: false };

    case 'toggleMute':
      return { ...state, muted: !state.muted };

    case 'setRepeat':
      return { ...state, repeat: action.repeat };

    case 'failed': {
      // A failed item must not be left as the current one, or every later action targets something
      // that cannot play. Skip forward from it directly — delegating to `next` does nothing here,
      // because `next` advances from `current`, which is still the failed track.
      const failed = state.failed.includes(action.id) ? state.failed : [...state.failed, action.id];
      const failedIndex = state.queue.findIndex((t) => t.id === action.id);
      if (failedIndex === -1) return { ...state, failed };

      const nextIndex = failedIndex + 1;
      if (nextIndex < state.queue.length) {
        return { ...state, failed, current: nextIndex, positionMs: 0, playing: true };
      }
      // Nothing after it. Keep the failed item selected so the failure stays visible, and stop.
      if (state.repeat === 'all') {
        return { ...state, failed, current: 0, positionMs: 0, playing: true };
      }
      return { ...state, failed, playing: false };
    }

    case 'clear':
      return { ...initialPlayerState, volume: state.volume, muted: state.muted, repeat: state.repeat };

    default:
      return state;
  }
}

/** mm:ss, or h:mm:ss when long enough to need it. */
export function formatTime(ms: number | undefined): string {
  if (ms === undefined || !isFinite(ms) || ms < 0) return '--:--';
  const total = Math.floor(ms / 1000);
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

/**
 * A conservative display name when nobody has named a record.
 *
 * The handoff is explicit that artist and title must not be fabricated, so this states only what
 * is verifiably true about the inscription. It is labelled `derived` so the UI never presents it
 * as a title someone chose.
 *
 * **The block height alone is not a name.** A single block can carry dozens of unrelated
 * inscriptions — block 775271 holds 26 distinct audio files, each a different recording — so
 * titling them all "Audio at Block 775271" produced a catalogue in which *70 of the first 100
 * rows were indistinguishable from one another*. The listing looked duplicated when it was not;
 * every row was a different piece of audio with the same label.
 *
 * So the record is disambiguated by the leading bytes of its own inscription id. That is the
 * canonical handle, it is stable across every surface (the row, the queue, the player bar), and it
 * is checkable — a reader can match the label to the id on the detail page. It makes no claim
 * about the sound, which is the point: a derived name must not pretend to be a title.
 */
export function derivedTitle(track: {
  inscriptionId?: string;
  blockHeight?: number;
  container?: string;
}): string {
  // The first 8 hex characters are enough to separate the rows a reader actually sees, and short
  // enough to survive the narrowest layout.
  const short = track.inscriptionId ? track.inscriptionId.slice(0, 8) : undefined;
  if (track.blockHeight !== undefined) {
    return short
      ? `Audio at Block ${track.blockHeight} · ${short}`
      : `Audio at Block ${track.blockHeight}`;
  }
  if (track.inscriptionId) {
    return `Inscription ${track.inscriptionId.slice(0, 8)}…`;
  }
  return 'Untitled inscription';
}
